import { useState, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/hooks/useSidebar';
import {
  BreadcrumbItem,
  ContentLayout,
  DashboardTab,
  getDefaultTabForRole,
} from '@/types/dashboard';
import AppHeader from './AppHeader';
import AppSidebar from './AppSidebar';
import AppProfilePopup from './AppProfilePopup';
import { useThemeColors } from '@/styles';
import { layout } from '@/styles/design-tokens';
import { WeekInfo } from '@/utils/week';
import { BoardPreferences } from '@/types/preferences.types';
import { TabScrollMode } from '@/types/dashboard';

/** Maps ContentLayout presets to max-width CSS values */
const CONTENT_MAX_WIDTH: Record<ContentLayout, string | undefined> = {
  [ContentLayout.FULL]: undefined,
  [ContentLayout.NARROW]: layout.contentWidth.narrow,
  [ContentLayout.MEDIUM]: layout.contentWidth.medium,
  [ContentLayout.WIDE]: layout.contentWidth.wide,
};

interface AppLayoutProps {
  children: ReactNode;
  className?: string;
  /** Content width preset — controls max-width of the main content area */
  contentLayout?: ContentLayout;
  breadcrumbs?: BreadcrumbItem[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  currentBoardId?: string;
  currentBoardName?: string;
  onBoardChange?: (boardId: string | undefined) => void;
  // Week filtering props
  showWeekNavigator?: boolean;
  selectedWeek?: WeekInfo;
  onWeekChange?: (week: WeekInfo) => void;
  // Board settings props
  showBoardSettings?: boolean;
  onBoardSettingsClick?: () => void;
  // Screen-specific header actions
  headerExtras?: ReactNode;
  boardBackground?: BoardPreferences['background'];
  scrollMode?: TabScrollMode;
}

/**
 * AppLayout - Main application layout wrapper
 *
 * Provides consistent header, sidebar, and profile popup across authenticated screens.
 * Sidebar is a floating drawer overlay — main content is always full-width.
 */
export default function AppLayout({
  children,
  className = '',
  contentLayout = ContentLayout.FULL,
  breadcrumbs = [],
  activeTab: activeTabProp,
  onTabChange: onTabChangeProp,
  currentBoardId,
  currentBoardName,
  onBoardChange,
  showWeekNavigator,
  selectedWeek,
  onWeekChange,
  showBoardSettings,
  onBoardSettingsClick,
  headerExtras,
  boardBackground,
  scrollMode = TabScrollMode.CONTAINED,
}: AppLayoutProps) {
  const { logout, user } = useAuth();
  const colors = useThemeColors();
  const sidebar = useSidebar();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Use props if provided, otherwise use sidebar hook
  const activeTab = activeTabProp ?? sidebar.activeTab;
  const setActiveTab = onTabChangeProp ?? sidebar.setActiveTab;
  const isCollapsed = sidebar.isCollapsed;
  const toggleSidebar = sidebar.toggleSidebar;
  const tabs = sidebar.tabs;

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
  };

  const handleLogoClick = () => {
    // Navigate to user's default tab based on their role
    if (user?.role) {
      const defaultTab = getDefaultTabForRole(user.role);
      setActiveTab(defaultTab);

      // Reset board to user's own board (undefined triggers loading user's board)
      if (defaultTab === DashboardTab.MY_BOARD) {
        handleBoardChange(undefined);
      }
    } else {
      // Fallback to my-board if role not available
      setActiveTab(DashboardTab.MY_BOARD);
      handleBoardChange(undefined);
    }
  };

  const handleBoardChange = (boardId: string | undefined) => {
    onBoardChange?.(boardId);
  };

  const isContained = scrollMode === TabScrollMode.CONTAINED;

  return (
    <div
      className={`${isContained ? 'h-screen' : 'min-h-screen'} flex flex-col min-w-[320px] ${className}`}
      style={{ backgroundColor: colors.background }}
    >
      {/* Sticky Header */}
      <AppHeader
        onSidebarToggle={toggleSidebar}
        isSidebarCollapsed={isCollapsed}
        breadcrumbs={breadcrumbs}
        onProfileClick={() => setIsProfileOpen(true)}
        onLogoClick={handleLogoClick}
        currentBoardId={currentBoardId}
        currentBoardName={currentBoardName}
        onBoardChange={onBoardChange ? handleBoardChange : undefined}
        showWeekNavigator={showWeekNavigator}
        selectedWeek={selectedWeek}
        onWeekChange={onWeekChange}
        showBoardSettings={showBoardSettings}
        onBoardSettingsClick={onBoardSettingsClick}
        headerExtras={headerExtras}
        boardBackground={boardBackground}
      />

      {/* Main Content Area */}
      <div className={`flex flex-1 ${isContained ? 'overflow-hidden' : ''}`}>
        {/* Sidebar (floating drawer overlay) */}
        <AppSidebar
          isCollapsed={isCollapsed}
          onToggle={toggleSidebar}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs}
        />

        {/* Main Content — width constrained by contentLayout preset */}
        <main className={`flex-1 ${isContained ? 'overflow-auto' : ''}`}>
          <div
            className={isContained ? 'h-full' : ''}
            style={
              CONTENT_MAX_WIDTH[contentLayout]
                ? {
                    maxWidth: CONTENT_MAX_WIDTH[contentLayout],
                    marginInline: 'auto',
                    width: '100%',
                  }
                : undefined
            }
          >
            {children}
          </div>
        </main>
      </div>

      {/* Profile Popup */}
      <AppProfilePopup
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onLogout={handleLogout}
      />
    </div>
  );
}
